import { forwardRef } from 'react';

export interface LoadingSpinnerProps {
  /** Size of the spinner */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Color variant */
  variant?: 'white' | 'primary' | 'blue' | 'gray';
  /** Additional CSS classes */
  className?: string;
  /** Accessible label for the spinner */
  label?: string;
}

const sizeClasses = {
  xs: 'h-3 w-3 min-h-3 min-w-3 border-2',
  sm: 'h-4 w-4 min-h-4 min-w-4 border-2',
  md: 'h-5 w-5 min-h-5 min-w-5 border-2',
  lg: 'h-8 w-8 min-h-8 min-w-8 border-4',
  xl: 'h-12 w-12 min-h-12 min-w-12 border-4',
};

// Border-based color variants for the spinner
const colorClasses = {
  white: 'border-white/25 border-t-white border-r-white/75',
  primary: 'border-gray-200 border-t-red-600 border-r-red-400',
  blue: 'border-gray-200 border-t-blue-600 border-r-blue-400',
  gray: 'border-gray-200 border-t-gray-600 border-r-gray-400',
};

/**
 * Reusable loading spinner component.
 * Uses the Flouri red gradient border-style spinner for consistency across the app.
 */
export const LoadingSpinner = forwardRef<HTMLDivElement, LoadingSpinnerProps>(
  ({ size = 'md', variant = 'primary', className = '', label = 'Loading' }, ref) => {
    return (
      <div
        ref={ref}
        className={`animate-spin rounded-full flex-shrink-0 aspect-square ${sizeClasses[size]} ${colorClasses[variant]} ${className}`}
        role="status"
        aria-label={label}
      />
    );
  }
);

LoadingSpinner.displayName = 'LoadingSpinner';

export default LoadingSpinner;
